import * as React from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Footer } from "@/ui/components/Footer"
import { MobileHeader } from "@/ui/components/MobileHeader"
import { Button } from "@/ui/components/ui/button"
import { NfcWirelessSvg, ViewCollectionSvg } from "@/ui/components/icons"
import { useAppSelector } from "@/store/hooks"
import { selectIsAuthenticated } from "@/store/slices/authSlice"

export interface WelcomeAfterLoginProps {
  /**
   * Handler for "PAIRING MY NEW ITEMS" button click
   */
  onPairingClick: () => void
  /**
   * Handler for "GO TO MY COLLECTION" button click
   */
  onCollectionClick: () => void
  /**
   * Optional className for custom styling
   */
  className?: string
}

/**
 * WelcomeAfterLogin Component
 *
 * Welcome page layout displayed after user login with two action buttons:
 * - "PAIRING MY NEW ITEMS" - Navigate to pairing flow
 * - "GO TO MY COLLECTION" - Navigate to Trophy Room
 *
 * Features:
 * - Background image (same as AuthLayout)
 * - MobileHeader with menu
 * - Transparent content container (no white card background)
 * - Two centered action buttons with icons
 * - Footer at the bottom
 * - Mobile-first responsive (max-w-[21.4375rem] = 343px)
 *
 * @example
 * ```tsx
 * <WelcomeAfterLogin
 *   onPairingClick={() => navigate('/owner/pairing')}
 *   onCollectionClick={() => navigate('/owner/trophy-room')}
 * />
 * ```
 */
export const WelcomeAfterLogin = React.forwardRef<
  HTMLDivElement,
  WelcomeAfterLoginProps
>(({ onPairingClick, onCollectionClick, className }, ref) => {
  const { t } = useTranslation()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  return (
    <div ref={ref} className="relative min-h-screen flex flex-col">
      {/* Background - all screens, full opacity */}
      <div
        className="absolute inset-0 bg-[url('/images/app-bg.jpg')] bg-cover bg-center pointer-events-none -z-10"
        aria-hidden="true"
      />

      {/* MobileHeader - same as AuthLayout */}
      <MobileHeader fixed={true} isAuthenticated={isAuthenticated} />

      {/* Content area - flex-1 to push footer down, centered */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        {/* Transparent container - no white background, centered buttons */}
        <div
          className={cn(
            "w-full max-w-[21.4375rem] flex flex-col items-center justify-center gap-4 relative z-10",
            className
          )}
        >
          {/* Pairing Button */}
          <Button
            variant="primary-dark"
            size="primary-dark"
            leftIcon={<NfcWirelessSvg className="w-4 h-4" />}
            onClick={onPairingClick}
            className="w-full max-w-[19.4375rem]"
          >
            {t("onboarding.welcome.pairingButton")}
          </Button>

          {/* Collection Button */}
          <Button
            variant="primary-dark"
            size="primary-dark"
            leftIcon={<ViewCollectionSvg className="w-4 h-4" />}
            onClick={onCollectionClick}
            className="w-full max-w-[19.4375rem]"
          >
            {t("onboarding.welcome.collectionButton")}
          </Button>
        </div>
      </div>

      {/* Footer - relative positioning */}
      <Footer className="relative z-10" />
    </div>
  )
})

WelcomeAfterLogin.displayName = "WelcomeAfterLogin"
